import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { allArticles } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import FooterSection from "@/pages/home/components/FooterSection";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://usfansspreadsheet.asia";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const article = allArticles.find((a) => a.slug === slug);
  const relatedArticles = allArticles.filter((a) => a.slug !== slug).slice(0, 4);

  if (!article) {
    return (
      <main className="bg-background-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-foreground-950 mb-4">Artykuł nie znaleziony</h1>
          <Link to="/" className="cursor-pointer inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium">
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Powrót do strony głównej
          </Link>
        </div>
      </main>
    );
  }

  const articleContent = getArticleContent(article.content);
  const articleUrl = `${siteUrl}/artykul/${article.slug}`;

  return (
    <main className="bg-background-50 min-h-screen">
      <Navbar />

      <div className="relative w-full h-56 md:h-72 bg-foreground-950 overflow-hidden pt-16">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-8">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/"
              className="cursor-pointer inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-4 transition-colors"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Strona główna
            </Link>
            <h1 className="font-heading text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-white leading-tight">
              {article.title}
            </h1>
          </div>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-400 mb-8 pb-8 border-b border-background-200">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
            Ekspert ds. danych i arkuszy kalkulacyjnych
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
            Aktualizacja: Czerwiec 2026
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
            8 min czytania
          </span>
        </div>

        <div className="prose-custom space-y-8 text-foreground-700">
          {articleContent}
        </div>

        <div className="mt-10 md:mt-14 p-6 md:p-8 bg-primary-50 rounded-2xl border border-primary-100">
          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-3">
            Gotowy, aby poznać pełne możliwości?
          </h3>
          <p className="text-sm text-foreground-600 mb-5">
            Sprawdź więcej informacji na stronie głównej USFansSpreadsheet i odkryj wszystkie funkcje.
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            Przejdź do kaspreadsheet.com
            <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
          </a>
        </div>

        <div className="mt-10 md:mt-14 pt-8 border-t border-background-200">
          <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
            Powiązane artykuły
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to="/"
              className="cursor-pointer group p-4 rounded-2xl border border-background-200 hover:border-background-300 transition-all"
            >
              <h4 className="text-sm font-medium text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors">
                Strona główna USFansSpreadsheet
              </h4>
              <p className="text-xs text-foreground-500">
                Wróć do głównego przewodnika z porównaniami, opiniami i FAQ.
              </p>
            </Link>
            {relatedArticles.slice(0, 3).map((rel) => (
              <Link
                key={rel.id}
                to={`/artykul/${rel.slug}`}
                className="cursor-pointer group p-4 rounded-2xl border border-background-200 hover:border-background-300 transition-all"
              >
                <h4 className="text-sm font-medium text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors">
                  {rel.title}
                </h4>
                <p className="text-xs text-foreground-500">
                  {rel.excerpt.substring(0, 80)}...
                </p>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="cursor-pointer inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Powrót do strony głównej
          </Link>
        </div>
      </article>

      <FooterSection />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "author": {
              "@type": "Person",
              "name": "Ekspert ds. danych i arkuszy kalkulacyjnych",
            },
            "datePublished": "2026-06-01",
            "dateModified": "2026-06-24",
            "inLanguage": "pl",
            "publisher": {
              "@type": "Organization",
              "name": "USFansSpreadsheet",
            },
            "url": articleUrl,
            "mainEntityOfPage": articleUrl,
            "image": article.image,
          }),
        }}
      />
    </main>
  );
}

function getArticleContent(slug: string) {
  const baseContent = (
    <>
      <section>
        <p className="text-base md:text-lg leading-relaxed">
          <strong className="text-foreground-900">USFansSpreadsheet</strong> to nowoczesne narzędzie do zarządzania arkuszami kalkulacyjnymi, które zdobywa coraz większą popularność wśród użytkowników na całym świecie. W tym artykule przedstawimy wszystkie kluczowe informacje, które pomogą Ci podjąć świadomą decyzję.
        </p>
      </section>
    </>
  );

  const contentMap: Record<string, JSX.Element> = {
    "przewodnik": (
      <>
        {baseContent}
        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">Co to jest USFansSpreadsheet?</h2>
          <p className="text-sm md:text-base leading-relaxed mb-4"><strong className="text-foreground-900">USFansSpreadsheet</strong> to zaawansowana platforma do tworzenia, edycji i zarządzania arkuszami kalkulacyjnymi online. W przeciwieństwie do tradycyjnych rozwiązań, takich jak Microsoft Excel czy Google Sheets, <strong className="text-foreground-900">USFansSpreadsheet</strong> oferuje unikalne połączenie wydajności, bezpieczeństwa i nowoczesnych funkcji automatyzacji.</p>
          <p className="text-sm md:text-base leading-relaxed">Kluczową przewagą <strong className="text-foreground-900">USFansSpreadsheet</strong> nad konkurencją jest połączenie trzech elementów: szybkości działania, kompleksowego bezpieczeństwa danych oraz zaawansowanych funkcji współpracy zespołowej.</p>
        </section>
        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">Jak działa USFansSpreadsheet?</h2>
          <p className="text-sm md:text-base leading-relaxed mb-4">Działanie <strong className="text-foreground-900">USFansSpreadsheet</strong> opiera się na zaawansowanej architekturze chmurowej, która zapewnia błyskawiczne przetwarzanie danych. System wykorzystuje inteligentne algorytmy do optymalizacji obliczeń i minimalizacji opóźnień.</p>
          <p className="text-sm md:text-base leading-relaxed">System oferuje również integracje z popularnymi narzędziami biznesowymi, co sprawia, że <strong className="text-foreground-900">USFansSpreadsheet</strong> staje się centralnym punktem zarządzania danymi w organizacji.</p>
        </section>
        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">7 powodów, dla których warto wybrać USFansSpreadsheet</h2>
          <div className="space-y-4">
            {["Intuicyjny interfejs użytkownika","Najwyższy poziom bezpieczeństwa","Zaawansowana automatyzacja","Współpraca w czasie rzeczywistym","Wydajność przy dużych zbiorach","Regularne aktualizacje","Integracje z narzędziami biznesowymi"].map((reason, i) => (
              <div key={i} className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-medium">{i+1}</span><p className="text-sm text-foreground-700"><strong className="text-foreground-900">{reason}</strong> – kluczowa zaleta <strong className="text-foreground-900">USFansSpreadsheet</strong> doceniana przez użytkowników na całym świecie.</p></div>
            ))}
          </div>
        </section>
      </>
    ),
    "bezpieczenstwo": (
      <>
        {baseContent}
        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">Standardy bezpieczeństwa w USFansSpreadsheet</h2>
          <p className="text-sm md:text-base leading-relaxed mb-4">Bezpieczeństwo danych to priorytet numer jeden <strong className="text-foreground-900">USFansSpreadsheet</strong>. Platforma wykorzystuje zaawansowane szyfrowanie end-to-end, co oznacza że Twoje dane są chronione zarówno podczas przesyłania, jak i przechowywania.</p>
          <p className="text-sm md:text-base leading-relaxed mb-4"><strong className="text-foreground-900">USFansSpreadsheet</strong> posiada certyfikat SOC 2 Type II oraz zgodność z ISO 27001. Regularne audyty bezpieczeństwa zapewniają najwyższy poziom ochrony.</p>
          <p className="text-sm md:text-base leading-relaxed">Wszystkie transmisje danych są chronione protokołem TLS 1.3, a system oferuje zaawansowane funkcje kontroli dostępu i szczegółowy dziennik audytu.</p>
        </section>
        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">Jak USFansSpreadsheet chroni Twoje dane?</h2>
          <p className="text-sm md:text-base leading-relaxed mb-4">Każda operacja na danych w <strong className="text-foreground-900">USFansSpreadsheet</strong> jest monitorowana i rejestrowana. System automatycznie wykrywa podejrzane aktywności i natychmiast powiadamia administratorów.</p>
          <p className="text-sm md:text-base leading-relaxed">Dodatkowo platforma oferuje uwierzytelnianie dwuskładnikowe i zaawansowane zarządzanie uprawnieniami użytkowników, co daje pełną kontrolę nad dostępem do danych.</p>
        </section>
      </>
    ),
  };

  return contentMap[slug] || (
    <>
      {baseContent}
      <section>
        <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">Szczegółowa analiza</h2>
        <p className="text-sm md:text-base leading-relaxed mb-4"><strong className="text-foreground-900">USFansSpreadsheet</strong> wyróżnia się na tle konkurencji pod wieloma względami. Niezależnie od tego, czy jesteś indywidualnym użytkownikiem, czy zarządzasz dużym zespołem, to narzędzie oferuje rozwiązania dopasowane do Twoich potrzeb.</p>
        <p className="text-sm md:text-base leading-relaxed mb-4">Kluczowe zalety <strong className="text-foreground-900">USFansSpreadsheet</strong> obejmują szybkość działania, intuicyjny interfejs oraz zaawansowane funkcje automatyzacji, które znacząco zwiększają produktywność.</p>
        <p className="text-sm md:text-base leading-relaxed">W porównaniu do tradycyjnych rozwiązań, <strong className="text-foreground-900">USFansSpreadsheet</strong> oferuje lepszą integrację z nowoczesnymi narzędziami biznesowymi i wyższy poziom bezpieczeństwa danych.</p>
      </section>
      <section>
        <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">Kluczowe funkcje i możliwości</h2>
        <div className="space-y-4">
          {["Zaawansowana automatyzacja procesów","Współpraca zespołowa w czasie rzeczywistym","Integracje z systemami CRM i e-commerce","Szyfrowanie end-to-end i certyfikaty bezpieczeństwa","Intuicyjny interfejs i łatwa obsługa","Wydajność przy dużych zbiorach danych"].map((feature, i) => (
            <div key={i} className="flex items-start gap-3"><span className="w-5 h-5 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-check-line text-xs"></i></span><p className="text-sm text-foreground-700">{feature} – to jedna z wyróżniających cech <strong className="text-foreground-900">USFansSpreadsheet</strong>.</p></div>
          ))}
        </div>
      </section>
      <section>
        <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">Podsumowanie</h2>
        <p className="text-sm md:text-base leading-relaxed"><strong className="text-foreground-900">USFansSpreadsheet</strong> to nowoczesne i kompleksowe rozwiązanie, które łączy zalety tradycyjnych arkuszy z zaawansowanymi funkcjami automatyzacji i najwyższym poziomem bezpieczeństwa. Jeśli zależy Ci na wydajności i bezpieczeństwie, jest to wybór, który spełni Twoje oczekiwania.</p>
      </section>
    </>
  );
}